import { useCallback, useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const inputCls = "bg-white border-zinc-300 text-zinc-800 focus-visible:ring-[#FF6B00] rounded-sm";

const LinkListEditor = ({ items, onChange, testidPrefix }) => {
  const set = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={it.id || i} className="flex items-center gap-2" data-testid={`${testidPrefix}-row-${i}`}>
          <div className="flex flex-col">
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-zinc-500 hover:text-[#FF6B00] disabled:opacity-30" aria-label="Move up"><ArrowUp className="h-3 w-3" /></button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-zinc-500 hover:text-[#FF6B00] disabled:opacity-30" aria-label="Move down"><ArrowDown className="h-3 w-3" /></button>
          </div>
          <Input placeholder="Label" value={it.label || ""} onChange={(e) => set(i, "label", e.target.value)} className={`${inputCls} w-40`} data-testid={`${testidPrefix}-label-${i}`} />
          <Input placeholder="/url or https://..." value={it.url || ""} onChange={(e) => set(i, "url", e.target.value)} className={`${inputCls} flex-1 font-mono text-xs`} data-testid={`${testidPrefix}-url-${i}`} />
          <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-zinc-500 hover:text-red-600 shrink-0" aria-label="Remove link" data-testid={`${testidPrefix}-remove-${i}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        onClick={() => onChange([...items, { id: `l-${Date.now()}`, label: "", url: "/" }])}
        className="border-zinc-300 text-zinc-600 rounded-sm text-xs uppercase font-bold"
        data-testid={`${testidPrefix}-add`}
      >
        <Plus className="h-3.5 w-3.5 mr-1" /> Add Link
      </Button>
    </div>
  );
};

export default function AdminNavigation() {
  const [nav, setNav] = useState(null);
  const [footer, setFooter] = useState(null);
  const [saving, setSaving] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [n, f] = await Promise.all([
        api.get("/admin/content/navigation"),
        api.get("/admin/content/footer"),
      ]);
      setNav(n.data.data);
      setFooter(f.data.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const saveNav = async (e) => {
    e.preventDefault();
    const clean = nav.items.filter((i) => i.label?.trim() && i.url?.trim());
    if (clean.length !== nav.items.length) {
      toast.error("Every menu item needs a label and a URL");
      return;
    }
    setSaving("nav");
    try {
      await api.put("/admin/content/navigation", { data: nav });
      toast.success("Navigation saved. Changes are live.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving("");
    }
  };

  const saveFooter = async (e) => {
    e.preventDefault();
    setSaving("footer");
    try {
      await api.put("/admin/content/footer", { data: footer });
      toast.success("Footer saved. Changes are live.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving("");
    }
  };

  if (!nav || !footer) {
    return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div>;
  }

  const setNavField = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setNav((n) => ({ ...n, [k]: v }));
  };
  const setFooterField = (k) => (e) => setFooter((f) => ({ ...f, [k]: e.target.value }));

  const setNavItem = (i, k, v) => setNav((n) => ({ ...n, items: n.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));

  const moveNavItem = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= nav.items.length) return;
    const next = [...nav.items];
    [next[i], next[j]] = [next[j], next[i]];
    setNav((n) => ({ ...n, items: next }));
  };

  return (
    <div data-testid="admin-navigation">
      <h1 className="font-display text-2xl font-black uppercase text-zinc-900 mb-6">Navigation & Footer</h1>

      <form onSubmit={saveNav} className="bg-white border border-zinc-200 p-6 mb-8">
        <h2 className="font-display text-lg font-bold uppercase text-zinc-900 mb-4">Header Menu</h2>
        <div className="space-y-2 mb-5">
          {nav.items.map((it, i) => (
            <div key={it.id || i} className="flex flex-wrap items-center gap-2" data-testid={`nav-item-row-${i}`}>
              <div className="flex flex-col">
                <button type="button" onClick={() => moveNavItem(i, -1)} disabled={i === 0} className="text-zinc-500 hover:text-[#FF6B00] disabled:opacity-30" aria-label="Move up"><ArrowUp className="h-3 w-3" /></button>
                <button type="button" onClick={() => moveNavItem(i, 1)} disabled={i === nav.items.length - 1} className="text-zinc-500 hover:text-[#FF6B00] disabled:opacity-30" aria-label="Move down"><ArrowDown className="h-3 w-3" /></button>
              </div>
              <Input placeholder="Label" value={it.label || ""} onChange={(e) => setNavItem(i, "label", e.target.value)} className={`${inputCls} w-36`} data-testid={`nav-item-label-${i}`} />
              <Input placeholder="/url" value={it.url || ""} onChange={(e) => setNavItem(i, "url", e.target.value)} className={`${inputCls} flex-1 min-w-[140px] font-mono text-xs`} data-testid={`nav-item-url-${i}`} />
              <label className="flex items-center gap-1.5 text-xs text-zinc-500">
                <input type="checkbox" checked={it.enabled !== false} onChange={(e) => setNavItem(i, "enabled", e.target.checked)} className="accent-[#FF6B00] h-3.5 w-3.5" data-testid={`nav-item-enabled-${i}`} />
                Show
              </label>
              <button type="button" onClick={() => setNav((n) => ({ ...n, items: n.items.filter((_, idx) => idx !== i) }))} className="text-zinc-500 hover:text-red-600" aria-label="Remove menu item" data-testid={`nav-item-remove-${i}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setNav((n) => ({ ...n, items: [...n.items, { id: `nav-${Date.now()}`, label: "", url: "/", enabled: true }] }))}
          className="border-zinc-300 text-zinc-600 rounded-sm text-xs uppercase font-bold mb-6"
          data-testid="nav-item-add"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Menu Item
        </Button>

        <h3 className="font-display text-base font-bold uppercase text-zinc-900 mb-3">Header CTA Button</h3>
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <label className="flex items-center gap-2 text-sm text-zinc-600">
            <input type="checkbox" checked={nav.cta_enabled !== false} onChange={setNavField("cta_enabled")} className="accent-[#FF6B00] h-4 w-4" data-testid="nav-cta-enabled" />
            Show CTA
          </label>
          <Input placeholder="CTA label" value={nav.cta_label || ""} onChange={setNavField("cta_label")} className={`${inputCls} w-48`} data-testid="nav-cta-label" />
          <Input placeholder="CTA URL" value={nav.cta_url || ""} onChange={setNavField("cta_url")} className={`${inputCls} w-48 font-mono text-xs`} data-testid="nav-cta-url" />
        </div>

        <Button type="submit" disabled={saving === "nav"} className="bg-[#FF6B00] hover:bg-[#FF8533] text-black font-bold uppercase tracking-wider rounded-sm" data-testid="nav-save-button">
          {saving === "nav" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Navigation"}
        </Button>
      </form>

      <form onSubmit={saveFooter} className="bg-white border border-zinc-200 p-6">
        <h2 className="font-display text-lg font-bold uppercase text-zinc-900 mb-4">Footer</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div className="sm:col-span-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Description</Label>
            <Textarea value={footer.description || ""} onChange={setFooterField("description")} className={`${inputCls} min-h-[70px]`} data-testid="footer-description-input" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Phone</Label>
            <Input value={footer.phone || ""} onChange={setFooterField("phone")} className={inputCls} data-testid="footer-phone-input" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Email</Label>
            <Input value={footer.email || ""} onChange={setFooterField("email")} className={inputCls} data-testid="footer-email-input" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Address</Label>
            <Input value={footer.address || ""} onChange={setFooterField("address")} className={inputCls} data-testid="footer-address-input" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Hours</Label>
            <Input value={footer.hours || ""} onChange={setFooterField("hours")} className={inputCls} data-testid="footer-hours-input" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Copyright (use {"{year}"} for the current year)</Label>
            <Input value={footer.copyright || ""} onChange={setFooterField("copyright")} className={inputCls} data-testid="footer-copyright-input" />
          </div>
        </div>
        <div className="mb-4">
          <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 block mb-2">Footer Links</Label>
          <LinkListEditor items={footer.links || []} onChange={(links) => setFooter((f) => ({ ...f, links }))} testidPrefix="footer-link" />
        </div>
        <div className="mb-5">
          <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 block mb-2">Social Links (label: facebook, instagram, x, youtube, linkedin)</Label>
          <LinkListEditor items={footer.social || []} onChange={(social) => setFooter((f) => ({ ...f, social }))} testidPrefix="social-link" />
        </div>
        <Button type="submit" disabled={saving === "footer"} className="bg-[#FF6B00] hover:bg-[#FF8533] text-black font-bold uppercase tracking-wider rounded-sm" data-testid="footer-save-button">
          {saving === "footer" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Footer"}
        </Button>
      </form>
    </div>
  );
}
